import * as React from 'react';
import {
  List,
  Datagrid,
  Edit,
  Create,
  SimpleForm,
  TextField,
  EditButton,
  DeleteButton,
  TextInput,
  NumberField,
  NumberInput,
  BooleanField,
  BooleanInput,
  required,
  regex,
  useTranslate,
  Toolbar,
  SaveButton,
} from 'react-admin';
import { Box, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';

import EmptyList from '../components/misc/EmptyList';
import { EditableImage } from '../components/form/EditableImageField';
import { resolveMediaUrl } from '../utils/mediaUrl';

const entity = 'radios';
const LOGO_SIZE = 160;

const urlValidate = regex(/^(https?:\/\/)[^\s]+$/i, 'Geçerli bir stream URL girin (https://...)');

const logoPreviewSrc = (logo) => {
  if (!logo) return null;
  if (typeof logo === 'string') return resolveMediaUrl(logo);
  if (logo?.src) {
    if (typeof logo.src === 'string' && logo.src.startsWith('blob:')) return logo.src;
    return resolveMediaUrl(logo.src);
  }
  if (logo?.rawFile instanceof Blob) {
    try {
      return URL.createObjectURL(logo.rawFile);
    } catch {
      return null;
    }
  }
  return null;
};

const RadioLivePreview = () => {
  const translate = useTranslate();
  const name = useWatch({ name: 'name' }) || translate('radios.preview_name', { _: 'Radyo adı' });
  const streamUrl = useWatch({ name: 'streamUrl' }) || '';
  const category = useWatch({ name: 'category' }) || '';
  const logo = useWatch({ name: 'logo' });
  const imgSrc = logoPreviewSrc(logo);

  return (
    <Box
      sx={{
        mt: 1,
        p: 2,
        borderRadius: 2,
        bgcolor: '#F7F7F8',
        border: '1px solid #E8E8E8',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#555' }}>
        {translate('radios.preview', { _: 'Önizleme' })}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            bgcolor: '#fff',
            border: '1px solid #eee',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {imgSrc ? (
            <Box
              component="img"
              src={imgSrc}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Typography sx={{ fontSize: 11, color: '#bbb' }}>Logo</Typography>
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{name}</Typography>
          {category ? (
            <Typography sx={{ fontSize: 11, color: '#888' }}>{category}</Typography>
          ) : null}
          {streamUrl ? (
            <Typography sx={{ fontSize: 10, color: '#AE256D', wordBreak: 'break-all', mt: 0.5 }}>
              {streamUrl}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

const RadioForm = () => {
  const translate = useTranslate();
  return (
    <>
      <TextInput
        source="name"
        label={translate('radios.name', { _: 'Radyo adı' })}
        validate={[required()]}
        fullWidth
      />
      <TextInput
        source="streamUrl"
        label={translate('radios.streamUrl', { _: 'Stream URL' })}
        validate={[required(), urlValidate]}
        fullWidth
        helperText="Canlı yayın adresi — örn. https://live.enerjiplay.com/stream/..."
      />
      <TextInput
        source="category"
        label={translate('radios.category', { _: 'Kategori' })}
        fullWidth
        helperText="Opsiyonel — Türkçe, Yabancı…"
      />
      <NumberInput
        source="order"
        label={translate('radios.order', { _: 'Sıra' })}
        defaultValue={0}
        fullWidth
      />
      <BooleanInput
        source="isActive"
        label={translate('radios.isActive', { _: 'Aktif' })}
        defaultValue={true}
      />
      <Box sx={{ mt: 1, mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          {translate('radios.logo', { _: 'Logo' })}
        </Typography>
        <EditableImage source="logo" width={LOGO_SIZE} height={LOGO_SIZE} aspect={1} />
      </Box>
      <RadioLivePreview />
    </>
  );
};

const FormToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

export const RadioList = () => (
  <List
    empty={<EmptyList resource={entity} />}
    sort={{ field: 'order', order: 'ASC' }}
    perPage={50}
  >
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <TextField source="name" label="Ad" />
      <TextField source="category" label="Kategori" />
      <TextField source="streamUrl" label="Stream URL" />
      <NumberField source="order" label="Sıra" />
      <BooleanField source="isActive" label="Aktif" />
      <EditButton />
      <DeleteButton mutationMode="pessimistic" />
    </Datagrid>
  </List>
);

export const RadioCreate = () => (
  <Create redirect="list">
    <SimpleForm toolbar={<FormToolbar />}>
      <RadioForm />
    </SimpleForm>
  </Create>
);

export const RadioEdit = () => (
  <Edit>
    <SimpleForm toolbar={<FormToolbar />}>
      <RadioForm />
    </SimpleForm>
  </Edit>
);
